import { useEffect, useMemo, useState } from "react";

import { api, useAlCambiar, useDatos } from "../api";
import { useAccion } from "../componentes/acciones";
import { Carga } from "../componentes/Carga";
import { DialogoAlta, DialogoDescarte } from "../componentes/Dialogos";
import { BadgeLead } from "../componentes/Estados";
import { Badge, Button, Card, Checkbox, DataTable, EmptyState, Input, Page, Select, Tabs, Text, TopBar, type DataTableColumn } from "../ds";
import { ESTADOS_LEAD, numero, PROVEEDORES, proveedor, rating, telefono } from "../formato";
import { Enlace, navegar, useUbicacion } from "../router";
import type { Lead, ListaLeads, Zona } from "../tipos";
import s from "./Leads.module.css";

const PLURALES: Record<string, string> = {
  nueva: "Nuevas",
  calificada: "Calificadas",
  contactada: "Contactadas",
  respondio: "Respondieron",
  reunion: "Reunión",
  cliente: "Clientes",
  descartada: "Descartadas",
  baja: "Bajas",
};

const FILTROS = ["estado", "zona", "minimo", "canal", "proveedor", "demo", "q", "orden", "pagina"] as const;

export function Leads() {
  const { params } = useUbicacion();
  const consulta = useMemo(() => {
    const p = new URLSearchParams();
    for (const k of FILTROS) {
      const v = params.get(k);
      if (v) p.set(k, v);
    }
    return p.toString();
  }, [params]);
  const { datos, error, cargando, recargar } = useDatos<ListaLeads>(`/api/leads?${consulta}`);
  const { datos: zonas } = useDatos<Zona[]>("/api/zonas");
  useAlCambiar(recargar);

  const [texto, setTexto] = useState(params.get("q") ?? "");
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [alta, setAlta] = useState(false);
  const [descarte, setDescarte] = useState(false);
  const { pendiente, ejecutar } = useAccion();

  // Cambiar un filtro vuelve a la primera página y limpia la selección.
  const poner = (cambios: Partial<Record<(typeof FILTROS)[number], string | null>>) => {
    const p = new URLSearchParams(consulta);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    if (!("pagina" in cambios)) p.delete("pagina");
    setSeleccion(new Set());
    const q = p.toString();
    navegar(q ? `/leads?${q}` : "/leads", { reemplazar: true });
  };

  // La búsqueda espera a que se deje de tipear.
  useEffect(() => {
    if (texto === (params.get("q") ?? "")) return;
    const t = setTimeout(() => poner({ q: texto.trim() || null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  const filas = datos?.filas ?? [];
  const todasMarcadas = filas.length > 0 && filas.every((f) => seleccion.has(f.id));
  const algunas = filas.some((f) => seleccion.has(f.id));
  const alternar = (id: string) =>
    setSeleccion((x) => {
      const n = new Set(x);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const columnas: DataTableColumn<Lead>[] = [
    {
      key: "sel",
      header: (
        <Checkbox
          aria-label="Seleccionar todas las de esta página"
          checked={todasMarcadas}
          indeterminate={algunas && !todasMarcadas}
          onChange={() => setSeleccion(todasMarcadas ? new Set() : new Set(filas.map((f) => f.id)))}
        />
      ),
      width: "44px",
      render: (l) => <Checkbox aria-label={`Seleccionar ${l.nombre}`} checked={seleccion.has(l.id)} onChange={() => alternar(l.id)} />,
    },
    {
      key: "nombre",
      header: "Inmobiliaria",
      sortable: true,
      width: "21%",
      render: (l) => (
        <span className={s.nombre}>
          <Enlace href={`/leads/${l.id}`} className={s.enlace}>
            {l.nombre}
          </Enlace>
          <Text as="span" size="xs" tone="muted" truncate>
            {l.resenas ? `${rating(l.rating)} en Google · ${l.resenas} reseñas` : l.localidad ?? "Sin reseñas en Google"}
          </Text>
        </span>
      ),
    },
    { key: "estado", header: "Estado", width: "112px", render: (l) => <BadgeLead estado={l.estado} /> },
    { key: "zona", header: "Zona", width: "10%", hideBelow: "md", render: (l) => <Text as="span" size="sm" truncate>{l.zona ?? "–"}</Text> },
    { key: "puntaje", header: "Puntaje", sortable: true, align: "right", mono: true, width: "88px", render: (l) => l.puntaje ?? "–" },
    { key: "email", header: "Email", hideBelow: "md", render: (l) => <Text as="span" size="sm" truncate>{l.email ?? "–"}</Text> },
    { key: "whatsapp", header: "WhatsApp", mono: true, width: "164px", hideBelow: "md", render: (l) => <span className={s.telefono}>{l.whatsapp ? telefono(l.whatsapp) : "–"}</span> },
    {
      key: "proveedor",
      header: "Sitio",
      width: "112px",
      hideBelow: "md",
      render: (l) =>
        l.proveedor ? (
          <Badge tone="outline" size="sm">
            {proveedor(l.proveedor)}
          </Badge>
        ) : (
          <Text as="span" size="xs" tone="muted">
            {l.sitioWeb ? "Sin detectar" : "Sin sitio"}
          </Text>
        ),
    },
    {
      key: "demo",
      header: "Demo",
      width: "116px",
      hideBelow: "md",
      render: (l) =>
        !l.demo ? (
          <Text as="span" size="xs" tone="muted">
            –
          </Text>
        ) : l.demo.visitas > 0 ? (
          <Badge tone="success" size="sm" dot>
            {l.demo.visitas === 1 ? "La abrió" : `La abrió · ${l.demo.visitas}`}
          </Badge>
        ) : (
          <Badge tone="brand" size="sm">
            Lista
          </Badge>
        ),
    },
  ];

  const hayFiltros = FILTROS.some((k) => k !== "pagina" && k !== "orden" && k !== "estado" && params.get(k));
  const orden = params.get("orden") ?? "puntaje";
  const ids = [...seleccion];

  const enLote = async (accion: "borradores" | "descartar", motivo?: string) => {
    const r = await ejecutar(
      accion,
      () => api<{ afectadas: number; omitidas: number }>("/api/leads/lote", { metodo: "POST", cuerpo: { ids, accion, motivo } }),
      (x) =>
        accion === "borradores"
          ? `${x.afectadas} borradores en la cola${x.omitidas ? ` · ${x.omitidas} no correspondían` : ""}`
          : `${x.afectadas} descartadas${x.omitidas ? ` · ${x.omitidas} ya estaban en otro estado` : ""}`,
    );
    if (r) {
      setSeleccion(new Set());
      setDescarte(false);
      recargar();
    }
  };

  const conteo = datos?.porEstado ?? {};
  const pestanas = [
    { value: "", label: "Todas", count: conteo.todas },
    ...ESTADOS_LEAD.filter((e) => e.valor !== "nueva" || conteo.nueva).map((e) => ({ value: e.valor, label: PLURALES[e.valor], count: conteo[e.valor] ?? 0 })),
  ];

  return (
    <>
      <TopBar
        title="Leads"
        subtitle="Todas las inmobiliarias encontradas en el AMBA, con su contacto y su puntaje."
        actions={
          <>
            <Button variant="outline" size="sm" icon="download" href={`/api/leads.csv?${consulta}`}>
              Exportar CSV
            </Button>
            <Button size="sm" icon="plus" onClick={() => setAlta(true)}>
              Agregar inmobiliaria
            </Button>
          </>
        }
      />
      <Page width="none">
        <Tabs value={params.get("estado") ?? ""} onChange={(v) => poner({ estado: v || null })} tabs={pestanas} />
        <div className={s.filtros}>
          <Input
            aria-label="Buscar"
            icon="search"
            type="search"
            placeholder="Buscar por nombre, email o dominio"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className={s.busqueda}
          />
          <Select
            variant="filter"
            aria-label="Zona"
            placeholder="Zona"
            value={params.get("zona") ?? ""}
            onChange={(e) => poner({ zona: e.target.value || null })}
            menuMinWidth={240}
            options={[
              { value: "", label: "Todas las zonas" },
              ...(zonas ?? []).filter((z) => z.inmobiliarias > 0).sort((a, b) => a.nombre.localeCompare(b.nombre, "es")).map((z) => ({ value: z.id, label: z.nombre, detail: String(z.inmobiliarias) })),
            ]}
          />
          <Select
            variant="filter"
            aria-label="Puntaje mínimo"
            placeholder="Puntaje"
            value={params.get("minimo") ?? ""}
            onChange={(e) => poner({ minimo: e.target.value || null })}
            options={[{ value: "", label: "Cualquier puntaje" }, ...["45", "60", "70", "80"].map((v) => ({ value: v, label: `${v} o más` }))]}
          />
          <Select
            variant="filter"
            aria-label="Canal"
            placeholder="Canal"
            value={params.get("canal") ?? ""}
            onChange={(e) => poner({ canal: e.target.value || null })}
            options={[
              { value: "", label: "Cualquier canal" },
              { value: "whatsapp", label: "Con WhatsApp" },
              { value: "email", label: "Con email" },
              { value: "sin_contacto", label: "Sin contacto" },
            ]}
          />
          <Select
            variant="filter"
            aria-label="Sitio"
            placeholder="Sitio"
            value={params.get("proveedor") ?? ""}
            onChange={(e) => poner({ proveedor: e.target.value || null })}
            options={[{ value: "", label: "Cualquier sitio" }, ...Object.entries(PROVEEDORES).map(([value, label]) => ({ value, label })), { value: "sin_detectar", label: "Sin detectar" }]}
          />
          <Select
            variant="filter"
            aria-label="Demo"
            placeholder="Demo"
            value={params.get("demo") ?? ""}
            onChange={(e) => poner({ demo: e.target.value || null })}
            options={[
              { value: "", label: "Con o sin demo" },
              { value: "con", label: "Con demo" },
              { value: "abierta", label: "La abrió" },
              { value: "sin", label: "Sin demo" },
            ]}
          />
          {hayFiltros ? (
            <Button size="sm" variant="ghost" icon="x" onClick={() => (setTexto(""), poner({ zona: null, minimo: null, canal: null, proveedor: null, demo: null, q: null }))}>
              Limpiar
            </Button>
          ) : null}
          <span className={s.espacio} />
          <Text as="span" size="xs" tone="muted">
            <strong className={s.cifra}>{datos ? numero(datos.total) : "–"}</strong> resultados
          </Text>
        </div>

        {seleccion.size > 0 ? (
          <Card tone="brand" padding="sm" className={s.lote}>
            <Text as="span" size="sm" weight="bold" tone="brand">
              {seleccion.size === 1 ? "1 seleccionada" : `${seleccion.size} seleccionadas`}
            </Text>
            <span className={s.espacio} />
            <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>
              Quitar selección
            </Button>
            <Button size="sm" variant="danger-soft" icon="x" onClick={() => setDescarte(true)}>
              Descartar
            </Button>
            <Button size="sm" icon="whatsapp" pending={pendiente === "borradores"} onClick={() => enLote("borradores")}>
              Preparar borradores de WhatsApp
            </Button>
          </Card>
        ) : null}

        {!datos ? (
          <Carga error={error} onReintentar={recargar} filas={10} />
        ) : (
          <DataTable
            columns={columnas}
            rows={filas}
            rowKey={(l) => l.id}
            rowHighlight={(l) => (seleccion.has(l.id) ? "brand" : l.estado === "descartada" || l.estado === "baja" ? "muted" : undefined)}
            loading={cargando}
            sortKey={orden === "nombre" ? "nombre" : "puntaje"}
            sortDirection={orden === "nombre" ? "asc" : "desc"}
            onSort={(k) => poner({ orden: k === "nombre" ? "nombre" : null })}
            page={datos.pagina}
            pageSize={datos.porPagina}
            total={datos.total}
            onPageChange={(p) => poner({ pagina: p > 1 ? String(p) : null })}
            minWidth="960px"
            empty={
              <EmptyState icon="search" title="No hay inmobiliarias con estos filtros" tone="plain">
                Probá sacando alguno.
              </EmptyState>
            }
          />
        )}
      </Page>
      <DialogoAlta abierto={alta} onCerrar={() => setAlta(false)} />
      <DialogoDescarte
        abierto={descarte}
        titulo={seleccion.size === 1 ? "Descartar 1 inmobiliaria" : `Descartar ${seleccion.size} inmobiliarias`}
        pendiente={pendiente === "descartar"}
        onCerrar={() => setDescarte(false)}
        onConfirmar={(motivo) => enLote("descartar", motivo)}
      />
    </>
  );
}
